import Table from 'react-bootstrap/Table';
import styles from './index.module.scss';
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addChosenUser } from '../../redux/slices/users.slice';
import Pagination from 'react-bootstrap/Pagination';
import Spinner from 'react-bootstrap/Spinner';

const USER_STATUS_BLOCKED = 2;

// Derive a display status from the user object
const getStatus = (item) => {
  if (item.status === USER_STATUS_BLOCKED) return 'blocked';
  if (item.subscription?.length > 0) return 'active';
  return 'no-subscription';
};

const STATUS_CONFIG = {
  active:          { label: 'Active',          className: 'badgeActive'   },
  'no-subscription': { label: 'No subscription', className: 'badgeNoSub' },
  blocked:         { label: 'Blocked',         className: 'badgeBlocked'  },
};

const COLUMNS = [
  { label: 'Studio name', field: 'studioName'  },
  { label: 'Name',        field: 'name'         },
  { label: 'Email',       field: 'email'        },
  { label: 'Subscription', field: null          },
  { label: 'Signup',      field: 'registrated'  },
  { label: 'Status',      field: null           },
];

function SortIcon({ field, sortField, sortDirection }) {
  if (!field || field !== sortField) {
    return <span className={styles.sortIconInactive}>↕</span>;
  }
  return <span className={styles.sortIconActive}>{sortDirection === 'DESC' ? '▼' : '▲'}</span>;
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return <span className={styles[config.className]}>{config.label}</span>;
}

function UsersTable() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { users, countUsers, error, loading } = useSelector(state => state.users);
  const { isAdmin, id } = useSelector(state => state.personality.user);

  const [page, setPage]                       = useState(1);
  const [filterValue, setFilterValue]         = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [sortField, setSortField]             = useState('registrated');
  const [sortDirection, setSortDirection]     = useState('DESC');
  const [statusFilter, setStatusFilter]       = useState('all');

  const countPages = Math.ceil(countUsers / 25);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(filterValue), 400);
    return () => clearTimeout(timer);
  }, [filterValue]);

  useEffect(() => {
    if (!isAdmin) return;
    dispatch({
      type: 'USERS_GET_ALL',
      payload: { userId: id, page, size: 25, sortField, sortDirection, filterValue: debouncedFilter },
    });
  }, [debouncedFilter, sortField, sortDirection, page]);

  const handleSearchChange = (e) => {
    setFilterValue(e.target.value);
    setPage(1);
  };

  const toggleSort = (field) => {
    if (!field) return;
    if (sortField === field) {
      setSortDirection(d => d === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
    setPage(1);
  };

  const onRowClick = (item) => {
    dispatch(addChosenUser(item));
    navigate(`${item.id}`);
  };

  const changePage = (n) => {
    const value = Math.min(Math.max(n, 1), countPages);
    setPage(value);
  };

  const getPaginationItems = () => {
    const length = Math.min(countPages, 3);
    return Array.from({ length }, (_, i) => (
      <Pagination.Item key={i} active={page === i + 1} onClick={() => changePage(i + 1)}>
        {i + 1}
      </Pagination.Item>
    ));
  };

  const filteredUsers = users.filter(item => {
    if (item.role === 1) return false;
    if (statusFilter === 'all') return true;
    return getStatus(item) === statusFilter;
  });

  return (
    <div>
      <div className={styles.toolbar}>
        <input
          type="text"
          placeholder="Search by name, email, or studio..."
          value={filterValue}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className={styles.statusSelect}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="no-subscription">No subscription</option>
          <option value="blocked">Blocked</option>
        </select>
        {countUsers > 0 && (
          <span className={styles.resultCount}>
            {countUsers} studio{countUsers !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {!loading ? (
        <>
          {error ? (
            <div className={styles.centerWrapper}>
              <div className="text-danger">{error}</div>
            </div>
          ) : (
            <>
              <Table hover striped={false}>
                <thead>
                  <tr>
                    {COLUMNS.map(({ label, field }) => (
                      <th
                        key={label}
                        onClick={() => toggleSort(field)}
                        className={field ? styles.sortableHeader : ''}
                      >
                        {label}
                        {field && <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((item, index) => {
                    const daysSince = Math.max(
                      1,
                      Math.floor((new Date() - new Date(item.registrated)) / (1000 * 60 * 60 * 24))
                    );
                    const subscription = item.subscription?.[0];
                    return (
                      <tr key={`usersTable-${index}`} onClick={() => onRowClick(item)}>
                        <td>{item.studioName}</td>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>
                          {subscription ? (
                            <>
                              <div>
                                {subscription.studiosNumber === null
                                  ? <span><b>Unlimited</b></span>
                                  : <><span><b>{subscription.studiosNumber}</b></span> </>}
                                Room{subscription.studiosNumber > 1 || subscription.studiosNumber === null ? 's, ' : ', '}
                                {subscription.stickersNumber !== null && (
                                  <><span><b>{subscription.stickersNumber}</b></span> </>
                                )}
                                Classe{subscription.stickersNumber > 1 || subscription.stickersNumber === null ? 's' : ''}
                              </div>
                              <div>Expires <b>{subscription.validUntil}</b></div>
                            </>
                          ) : <div>—</div>}
                        </td>
                        <td>{daysSince} day{daysSince !== 1 ? 's' : ''}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <StatusBadge status={getStatus(item)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {filteredUsers.length === 0 && (
                <div className={styles.emptyState}>No studios match your search.</div>
              )}

              {countPages > 1 && (
                <Pagination>
                  <Pagination.First onClick={() => changePage(1)} />
                  <Pagination.Prev onClick={() => changePage(page - 1)} />
                  {page < 3 ? (
                    <>{getPaginationItems()}</>
                  ) : (
                    <>
                      <Pagination.Item active={page === 1} onClick={() => changePage(1)}>1</Pagination.Item>
                      {page > 3 && <Pagination.Ellipsis />}
                      <Pagination.Item onClick={() => changePage(page - 1)}>{page - 1}</Pagination.Item>
                      {countPages === 3 && <Pagination.Item active onClick={() => changePage(page)}>{page}</Pagination.Item>}
                      {countPages !== page && <Pagination.Item active onClick={() => changePage(page)}>{page}</Pagination.Item>}
                      {countPages !== page + 1 && countPages !== page && (
                        <Pagination.Item onClick={() => changePage(page + 1)}>{page + 1}</Pagination.Item>
                      )}
                    </>
                  )}
                  {countPages !== 2 && countPages !== 3 && (
                    <>
                      {page < countPages - 2 && <Pagination.Ellipsis />}
                      <Pagination.Item active={page === countPages} onClick={() => changePage(countPages)}>
                        {countPages}
                      </Pagination.Item>
                      <Pagination.Next onClick={() => changePage(page + 1)} />
                      <Pagination.Last onClick={() => changePage(countPages)} />
                    </>
                  )}
                </Pagination>
              )}
            </>
          )}
        </>
      ) : (
        <div className={styles.centerWrapper}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}
    </div>
  );
}

export default UsersTable;
